/**
 * Customer dashboard unmask API (OTP-gated temporary reveal).
 *
 * When CUSTOMER_RESPONSE_MASKING_ENABLED is on, dashboard responses fetched
 * with a normal customer access token come back masked (PAN, DOB, email,
 * custom service text, ai_agent1_response, …). To temporarily see the real
 * values the customer proves identity over OTP and receives a SHORT-LIVED
 * access token scoped to unmasked responses.
 *
 * Endpoints (verified against /openapi.json):
 *   POST /api/v1/customer/unmask/init    → sends OTP to the customer's mobile, returns { exp }
 *   POST /api/v1/customer/unmask/verify  → exchanges OTP for { access_token, expires_in }
 *
 * `exp`        — seconds the OTP challenge stays valid (drives the dialog countdown).
 * `expires_in` — lifetime (seconds) of the returned unmask access token.
 *
 * NOTE: token storage + the auto-revert timer live in the unmask context
 * (features/unmask), NOT here. These hooks are thin transport wrappers so the
 * challenge state machine can sequence "swap token → refetch" itself.
 */
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import type { ApiError } from '@finmin/api-client';
import { type Schemas } from '@finmin/api-client';
import { apiClient } from '@/lib/api';

// ---------- types (from generated schema) -----------------------------------

export type UnmaskInitResponse = Schemas['CustomerUnmaskInitResponse'];
export type UnmaskVerifyRequest = Schemas['CustomerUnmaskVerifyRequest'];
export type UnmaskVerifyResponse = Schemas['CustomerUnmaskVerifyResponse'];

// ---------- raw API functions -----------------------------------------------

/** POST /api/v1/customer/unmask/init — sends a 6-digit OTP to the authenticated customer's mobile. */
export async function initUnmask() {
  const { data } = await apiClient.post<UnmaskInitResponse>('/api/v1/customer/unmask/init');
  return data;
}

/** POST /api/v1/customer/unmask/verify — exchanges the OTP for a short-lived unmask access token. */
export async function verifyUnmask(payload: UnmaskVerifyRequest) {
  const { data } = await apiClient.post<UnmaskVerifyResponse>(
    '/api/v1/customer/unmask/verify',
    payload,
  );
  return data;
}

// ---------- hooks -----------------------------------------------------------

export function useInitUnmask(
  options?: UseMutationOptions<UnmaskInitResponse, ApiError, void>,
) {
  return useMutation<UnmaskInitResponse, ApiError, void>({
    mutationFn: () => initUnmask(),
    ...options,
  });
}

export function useVerifyUnmask(
  options?: UseMutationOptions<UnmaskVerifyResponse, ApiError, UnmaskVerifyRequest>,
) {
  return useMutation<UnmaskVerifyResponse, ApiError, UnmaskVerifyRequest>({
    mutationFn: verifyUnmask,
    ...options,
  });
}
